/**
 * MassageConsent Component
 *
 * Presentational component for the Massage Therapy consent form.
 * Displays the consent text and signature canvas.
 *
 * Props:
 *   - checked: bool - Whether the massage section is enabled
 *   - onChange: func(fieldName, value) - Callback for changes
 *   - answers: obj - Current values for massage-related fields
 *   - signatureRef: ref - React ref for the signature canvas
 *   - onClearSignature: func - Callback to clear the signature
 *   - readOnly: bool (optional) - If true, disable interaction (for historical view)
 *   - version: obj (optional) - Version metadata for historical rendering
 */

import React from "react";
import {
  Typography,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
} from "@material-ui/core";
import SignatureCanvas from "react-signature-canvas";

const MASSAGE_VERSION_IDS = {
  V1: "1.0",
  V2: "2.0",
};

function MassageConsentTextV1() {
  return (
    <Typography variant="body1" style={{ textAlign: "left" }}>
      Please carefully read this information, and if you have any questions,
      please ask our practitioner(s).
      <br /> <br />
      The information received and collected about our clients/patients from
      their visit(s) to Appleby Wellness Centre is private and confidential. It
      is only used and viewed by the healthcare professionals and staff employed
      or contracted by Appleby Wellness Centre, unless, in the best interest of
      the client/patient, a practitioner determines that there is a need to
      communicate with another person or healthcare professional outside of
      Appleby Wellness Centre (also, we will not give, share, sell, or transfer
      any personal information to a third party unless required by law). Appleby
      Wellness Centre communicates with our clients by mail, email and or other
      electronic channels. On occasion, we may use client/patient information to
      conduct clinical studies to help us improve upon services provided.
      Appleby Wellness Centre may allow interns and students to get access to
      clinical files to clinical files for observations and clinical placements
      purposes.
      <br /> <br />
      "The practice of massage therapy is the assessment of the soft tissue and
      joints of the body and the treatment and prevention of physical
      dysfunction and pain of the soft tissues and joints by manipulation to
      develop, maintain, rehabilitate or augment physical function, or relieve
      pain." Massage Therapy Act, 1991
      <br /> <br />
      All information collected is to help formulate a clinical understanding of
      your condition and will be kept confidential, except where required by
      law, or to facilitate a diagnosis. A signed consent form is required
      before I contact any other health provider for your personal health
      information. An up to date and accurate health history is essential in the
      delivery of appropriate techniques involved with massage therapy
      treatments. Please advise Appleby Wellness Centre and practitioner(s) if
      you are receiving care from another health care provider for any condition
      you have and a complete list of all medications, including over the
      counter drugs and supplements and what condition they are treating, which
      will allow the practitioner(s) to determine if any modifications need to
      be made to your treatment. Please inform the practitioner(s) of any
      allergy or hypersensitivity reaction you may have to any oils, lotions, or
      laundry detergents, fabric softeners etc.
      <br /> <br />
      An assessment of your condition or physical discomforts may be necessary
      to determine the best course of action for treatment and your first
      treatment has been allotted extra time for assessment purposes. Throughout
      the course of treatments, additional time for reassessments may be
      required and may cut into your appointment time. Benefits of massage
      therapy services can include but are not limited to increased mobility,
      increased flexibility, decreased stress or anxiety, and relief of muscle
      or joint pain. Potential risks and or side effects associated with massage
      therapy can include, but are not limited to, bruising, swelling, tissue
      congestion, nausea, dizziness, temporary muscle tenderness or soreness,
      and aggravation of existing symptoms.
      <br /> <br />
      After every massage treatment it is important to stay hydrated by drinking
      plenty of water to help flush out any toxins released from the tissues
      during the massage, unless fluid intake is restricted by a physician. It
      is also advised that you apply cold to any tender or sore areas to
      decrease tissue congestion and chances of bruising. Results often vary
      from person to person depending on the goals of the individual. Following
      home care suggestions will help you achieve your goals; however, it may be
      necessary to seek out other health care providers to assist in achieving
      your health goals.
      <br /> <br />I have read the above statement and acknowledge that I am
      able to withdraw my consent at any time and discontinue treatment at any
      time. I also understand that I have the right to ask any questions I have
      about any aspect of the assessment, treatment or about any of the
      information being collected as they relate to me and my treatment plan.
    </Typography>
  );
}

function MassageConsentTextV2({ answers, onChange, readOnly }) {
  return (
    <>
      <Typography variant="body1" style={{ textAlign: "left" }}>
        <b>
          Massage Therapy Informed Consent, Treatment Authorization, and Clinic
          Policy Agreement
        </b>
        <br />
        <br />
        Please read this document carefully before treatment begins. If you have
        any questions regarding massage therapy treatment, assessment
        procedures, risks, billing, privacy, or clinic policies, please ask your
        Registered Massage Therapist (RMT) before signing.
        <br />
        <br />
        <b>1. Nature of Massage Therapy Treatment</b>
        <br />
        Massage therapy is a regulated health profession in Ontario governed by
        the Massage Therapy Act, 1991. Massage therapy involves the assessment
        of soft tissues, muscles, fascia, connective tissue, and joints of the
        body, and the treatment and prevention of physical dysfunction, pain,
        injury, and limitations through various manual and therapeutic
        techniques. Treatment techniques may include, but are not limited to:
        Swedish massage techniques// Deep tissue massage//Trigger point
        therapy//Myofascial release//Stretching and mobilization//Manual
        pressure techniques//Joint movement techniques//Hydrotherapy
        recommendations//Instrument-assisted soft tissue techniques//Use of
        massage tools or therapeutic devices composed of stone, metal, bone,
        plastic, silicone, wood, or other therapeutic materials//Use of elbows,
        knuckles, forearms, thumbs, fingers, and body-weight-assisted pressure
        techniques, Treatment intensity and pressure may vary depending on
        clinical presentation, tissue response, patient goals, and therapeutic
        necessity.
        <br />
        <br />
        <b>2. Acknowledgment of Risks and Potential Side Effects</b>
        <br />
        I understand that massage therapy, including deep tissue work, trigger
        point therapy, instrument-assisted techniques, and pressure-based
        treatment methods, may involve certain inherent risks and side effects.
        Possible risks and side effects include, but are not limited to:
        Temporary pain or discomfort//Post-treatment soreness or
        tenderness//Bruising//Swelling or inflammation//Tissue irritation or
        congestion//Fatigue//Dizziness or light-headedness//Headache//Temporary
        aggravation of existing symptoms//Reduced mobility for a temporary
        period following treatment//Muscle spasm or protective guarding//Skin
        sensitivity or irritation//Temporary nerve irritation
        symptoms//Emotional release or stress response.
        <br />
        I understand that the duration and severity of post-treatment symptoms
        may vary from person to person, depending on: Existing medical
        conditions//Tissue sensitivity//Chronic inflammation//Previous
        injuries//Physical condition//Medications//Hydration status//Daily
        activities//Exercise following treatment//Compliance with post-treatment
        recommendations. <br />
        I understand that although uncommon, some individuals may experience
        significant discomfort or symptom aggravation following treatment. I
        acknowledge that no guarantees or warranties have been made regarding
        treatment outcomes.
        <br />
        <br />
        <b>3. Patient Responsibilities and Disclosure Obligations</b>
        <br />
        I understand that it is my responsibility to provide complete, truthful,
        and accurate health information before and during treatment. I agree to
        inform the clinic and practitioner regarding: Current or past medical
        conditions//Injuries//Surgeries//Imaging results//Medications//Pregnancy
        status//Allergies or sensitivities//Neurological
        symptoms//Cardiovascular conditions//Acute pain conditions//Current
        healthcare providers involved in my care//Changes in symptoms or health
        status.
        <br />
        I understand that failure to disclose relevant medical information may
        increase the risk of adverse responses to treatment.
        <br />
        I understand that massage therapy is not a substitute for medical
        diagnosis or medical treatment.
        <br />
        I understand that the practitioner may recommend referral to another
        healthcare professional if symptoms appear outside the scope of massage
        therapy practice.
        <br />
        <br />
        <b>4. Consent to Assessment and Treatment</b>
        <br />
        I voluntarily consent to assessment and massage therapy treatment
        provided by the Registered Massage Therapist.
        <br />
        I understand that treatment techniques may be modified during the
        appointment based on tissue response, assessment findings, pain
        presentation, safety considerations, and clinical judgment.
        <br />
        I understand that I may ask questions regarding treatment techniques at
        any time.
        <br />
        I understand that I may request modification of pressure, discontinue a
        technique, or withdraw consent for treatment at any time.
        <br />
        I understand that the practitioner has the right to discontinue
        treatment when clinically indicated, when patient safety may be
        compromised, or when inappropriate conduct occurs.
        <br />
        <br />
        <b>5. Post-Treatment Care and Activity Acknowledgment</b>
        <br />
        I understand that following massage therapy treatment, particularly deep
        tissue, trigger point, or pressure-based techniques, temporary tissue
        sensitivity and soreness may occur.
        <br />
        I acknowledge that I may be advised to: Rest following treatment//Avoid
        strenuous physical activity//Avoid intense exercise or athletic
        training//Avoid repetitive strain activities//Hydrate adequately//Apply
        heat or cold as directed//Monitor symptoms//Seek medical attention if
        symptoms worsen or persist.
        <br />
        I understand that participation in strenuous physical activity,
        exercise, sports, gym training, aqua training, repetitive occupational
        activity, or other physically demanding activity immediately following
        treatment may contribute to increased soreness, irritation,
        inflammation, or aggravation of symptoms. I acknowledge that
        post-treatment recommendations are provided for my safety and recovery
        and that failure to follow recommendations may affect treatment
        response.
        <br />
        <br />
        <b>6. Privacy and Confidentiality</b>
        <br />
        All personal health information collected by Appleby Wellness Centre is
        handled in accordance with applicable privacy legislation and
        professional standards.
        <br />
        Information may be collected, used, stored, and disclosed for purposes
        including: Assessment and treatment//Clinical documentation//Billing and
        insurance claims//Quality assurance//Risk management//Legal and
        regulatory compliance//Communication with authorized healthcare
        providers//Regulatory investigations where required by law.
        <br />
        Clinical files may be reviewed by authorized staff, regulated healthcare
        professionals, students, interns, auditors, insurers, legal
        representatives, or regulatory bodies where permitted or required by
        law.
        <br />
        Electronic communication, including email and electronic claims
        processing, carries inherent privacy and cybersecurity risks. By
        communicating electronically with the clinic, I acknowledge and accept
        these risks.
        <br />
        <br />
        <b>7. Direct Billing and Insurance Acknowledgment</b>
        <br />
        I understand that direct billing is offered as a convenience only and is
        not guaranteed.
        <br />
        I acknowledge that: I remain financially responsible for all services
        rendered//Insurance approval does not guarantee clinical necessity or
        insurer payment//The clinic may reverse, correct, or resubmit claims
        where necessary//Insurers may audit claims, request records, investigate
        claims, or recover overpayments//Any overpayment recovery process must
        occur directly between the clinic and the insurer//Submission of
        inaccurate or fraudulent claims may result in denial of benefits,
        repayment obligations, insurer investigation, regulatory reporting, or
        legal consequences.
        <br />
        I authorize the clinic to communicate directly with insurers, benefits
        administrators, auditors, and authorized representatives for purposes
        relating to claims administration, verification, investigations, audits,
        overpayments, and payment recovery.
        <br />
        <br />
        <b>8. Limitation of Expectations</b>
        <br />
        I understand that massage therapy outcomes vary and depend on multiple
        factors beyond the practitioner’s control. I acknowledge that: Temporary
        symptom aggravation may occur even where treatment is appropriately
        delivered//Delayed onset soreness or discomfort may occur following
        treatment//Multiple treatments or referral to additional healthcare
        providers may be necessary//No specific outcome, cure, improvement, or
        timeline of recovery has been guaranteed.
        <br />
        <br />
        <b>9. Consent Confirmation</b>
        <br />
        By signing below, I confirm that:
        <br />
        <ul style={{ marginTop: 8, marginBottom: 8 }}>
          <li>I have read and understood this document in full</li>
          <li>I have had the opportunity to ask questions</li>
          <li>My questions have been answered to my satisfaction</li>
          <li>I understand the nature of massage therapy treatment</li>
          <li>
            I understand the potential risks, side effects, and limitations
          </li>
          <li>I voluntarily consent to assessment and treatment</li>
          <li>I understand that I may withdraw consent at any time</li>
          <li>
            I authorize collection, use, and disclosure of information as
            outlined above
          </li>
          <li>
            I understand and accept clinic policies relating to treatment,
            documentation, billing, and insurer communication
          </li>
        </ul>
        <br />
        <br />
        <b>Optional Acknowledgments</b>
      </Typography>
      <br />
      <Typography variant="body1" style={{ textAlign: "left" }}>
        Please review and acknowledge each statement below:
      </Typography>
      <FormControlLabel
        control={
          <Checkbox
            checked={answers.massage_postTreatmentSoreness || false}
            onChange={(e) =>
              onChange("massage_postTreatmentSoreness", e.target.checked)
            }
            color="primary"
            disabled={readOnly}
          />
        }
        label="I understand that post-treatment soreness or temporary aggravation of symptoms may occur."
      />{" "}
      <br />
      <FormControlLabel
        control={
          <Checkbox
            checked={answers.massage_deepTissue || false}
            onChange={(e) => onChange("massage_deepTissue", e.target.checked)}
            color="primary"
            disabled={readOnly}
          />
        }
        label="I understand that deep tissue, trigger point, and instrument-assisted techniques may cause tenderness or bruising."
      />
      <br />
      <FormControlLabel
        control={
          <Checkbox
            checked={answers.massage_strenuousActivity || false}
            onChange={(e) =>
              onChange("massage_strenuousActivity", e.target.checked)
            }
            color="primary"
            disabled={readOnly}
          />
        }
        label="I understand that strenuous activity immediately following treatment may increase post-treatment soreness or irritation."
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={answers.massage_communicateDiscomfort || false}
            onChange={(e) =>
              onChange("massage_communicateDiscomfort", e.target.checked)
            }
            color="primary"
            disabled={readOnly}
          />
        }
        label="I understand that I should communicate discomfort during treatment so pressure or techniques may be adjusted."
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={answers.massage_noSpecificOutcome || false}
            onChange={(e) =>
              onChange("massage_noSpecificOutcome", e.target.checked)
            }
            color="primary"
            disabled={readOnly}
          />
        }
        label="I understand that no specific treatment outcome or recovery timeline can be guaranteed."
      />
      <br />
    </>
  );
}

function MassageConsent({
  checked,
  onChange = () => {},
  answers = {},
  signatureRef,
  onClearSignature,
  readOnly = false,
  version,
}) {
  if (!checked) {
    return null;
  }

  const activeVersionId = version?.versionId || MASSAGE_VERSION_IDS.V2;

  return (
    <div>
      {/* Historical disclaimer if viewing old version */}
      {readOnly && version && (
        <Card style={{ marginBottom: 16, backgroundColor: "#fffacd" }}>
          <CardContent>
            <Typography variant="body2" style={{ color: "#856404" }}>
              <b>Historical View:</b> This form was submitted on{" "}
              {version.submittedAt?.split("T")[0]} under consent version{" "}
              {version.versionId} and is displayed for reference only.
            </Typography>
          </CardContent>
        </Card>
      )}
      <Typography variant="h4" style={{ textAlign: "left" }}>
        Massage Therapy Consent Form
      </Typography>
      <br />
      <br />
      {activeVersionId === MASSAGE_VERSION_IDS.V1 ? (
        <MassageConsentTextV1 />
      ) : (
        <MassageConsentTextV2
          answers={answers}
          onChange={onChange}
          readOnly={readOnly}
        />
      )}
      <br /> <br />
      <Typography variant="body1" style={{ textAlign: "left" }}>
        Please sign in the box below.
      </Typography>
      <div
        style={{
          borderStyle: "dotted",
          borderWidth: 2,
          borderColor: "#D3D3D3",
          width: 530,
          height: 240,
          borderRadius: 4,
        }}
      >
        <SignatureCanvas
          penColor="black"
          ref={signatureRef}
          canvasProps={{
            width: 530,
            height: 240,
            className: "consentMassageSig",
          }}
          disabled={readOnly}
        />
      </div>
      <br />
      {!readOnly && <Button onClick={onClearSignature}>Clear</Button>}
      <br />
    </div>
  );
}

export default MassageConsent;
